import { faCheckSquare, faEdit, faTrashAlt } from '@fortawesome/free-regular-svg-icons'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ButtonGroup } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import AppContext from '../../Authentication/AppContext'
import Authorization from '../../Authentication/Authorization'
import AuthService from '../../Authentication/AuthService'
import DialogContext from '../../Authentication/DialogContext'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable, { CaptionButton } from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import { FlipCard } from '../../components/FlipCard'
import ProtectedButton from '../../components/ProtectedButton'
import Loading from '../../layout/Loading'
import SolrConfigService from '../../service/SolrConfigService'
import TlsService from '../../service/TlsService'
import { formatBooleanColumn } from '../../utils/DatatableUtil'
import GatewayType from '../GatewayType'

const SolrConfig: GatewayType = () => {
  const { t } = useTranslation()
  const [solrConfig, setSolrConfig] = useState({} as any)
  const [solrConnection, setSolrConnection] = useState({} as any)
  const [solrCollections, setSolrCollections] = useState([] as any[])

  const [loading, setLoading] = useState(true)
  const [keysWithCerts, setKeysWithCerts] = useState([] as any[])
  const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    async function loadData() {
      await TlsService.getTlsKeysWithCerts(setKeysWithCerts)
      const config = await SolrConfigService.getSolrConfig()
      setSolrConfig(config)
    }

    loadData().finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: 'label',
        dataField: 'label',
        classes: 'font-weight-bolder',
        sort: true,
      },
      {
        text: 'value',
        dataField: 'value',
        sort: true,
        formatter: (cell: any, row: any) => (row.label === t('disabled') ? formatBooleanColumn(cell) : cell),
      },
    ]
  }, [t])

  const connectionColumns = useMemo(() => {
    return [
      {
        text: t('solrConnection.clientUrl'),
        dataField: 'connectionString',
        sort: true,
      },
      {
        text: t('solrConnection.connectionInfo'),
        dataField: 'ok',
        sort: true,
        formatter: (cell: any) => {
          if (cell === true) {
            return t('solrConnection.nodeRunning')
          } else {
            return t('solrConnection.nodeStopped')
          }
        },
      },
      {
        text: t('solrConnection.addInfo1'),
        dataField: 'leader',
        sort: true,
        formatter: (cell: any) => {
          if (cell === true) {
            return t('solrConnection.leader')
          } else if (cell === false) {
            return t('solrConnection.follower')
          }
        },
      },
      {
        text: t('solrConnection.addInfo2'),
        dataField: 'followersAvailable',
        sort: true,
        formatter: (cell: any) => {
          if (cell !== undefined) {
            return t('solrConnection.followersAvailable', { followersAvailable: cell })
          }
        },
      },
    ]
  }, [t])

  const data = getData(solrConfig, keysWithCerts, t)

  var captionButtons: CaptionButton[] = [
    {
      onClick: () => editSolrConfigDialog(t, editDialogPropsSetter, setSolrConfig, solrConfig, keysWithCerts),
      text: t('configure'),
      icon: faEdit,
      permissions: Authorization.settings.solrConfig.edit,
    },
  ]
  if (Object.keys(solrConfig).length > 0) {
    captionButtons.push({
      onClick: () => deleteSolrConfigDialog(t, confirmDialogPropsSetter, setSolrConfig),
      variant: 'outline-danger',
      icon: faTrashAlt,
      permissions: Authorization.settings.solrConfig.edit,
    })
  }
  var isSolrCollectionPresent = solrCollections !== undefined && solrCollections.length > 0
  var isSolrConnectionPresent = solrConnection.solrStatus !== undefined && solrConnection.solrStatus.length > 0
  return (
    <Loading isLoading={loading}>
      <DataTable
        keyField='label'
        data={data}
        columns={columns}
        headerClasses='d-none'
        searchable={false}
        caption={t('solrConfig.text')}
        helpLink={t('solrConfig.helpLink')}
        captionButtons={captionButtons}
      />

      <DataTable
        data={isSolrConnectionPresent ? solrConnection.solrStatus : []}
        columns={connectionColumns}
        searchable={false}
        caption={t('solrConnection.solrConnection')}
        captionButtons={[
          {
            onClick: () => testConnection(setSolrConnection, setSolrCollections),
            text: t('solrConnection.testConnection'),
            icon: faCheckSquare,
            permissions: Authorization.settings.solrConfig.edit,
          },
        ]}
      />
      {isSolrCollectionPresent && (
        <>
          <h3>Solr Collections</h3>
          <ul>
            {solrCollections.map((collection) => (
              <li>{collection}</li>
            ))}
          </ul>
        </>
      )}
    </Loading>
  )
}

function getData(solrConfig: any, keysWithCerts: any, t: TFunction) {
  return Object.keys(solrConfig).length > 0
    ? [
        {
          label: t('zookeeperHosts.text'),
          value: solrConfig.zookeeperHosts?.join(', '),
        },
        {
          label: t('solrUrls.text'),
          value: solrConfig.solrUrls?.join(', '),
        },
        {
          label: t('username.text'),
          value: solrConfig.username,
        },
        {
          label: t('password.text'),
          value: solrConfig.password ? '<Protected>' : '',
        },
        {
          label: t('keys.keyName'),
          value: solrConfig.tlsEnabled
            ? keysWithCerts === null || keysWithCerts.length === 0
              ? ''
              : keysWithCerts.find((key: any) => key.id === Number(solrConfig.keyId)).name
            : '',
        },
        {
          label: t('certificate.text'),
          value: solrConfig.tlsEnabled
            ? keysWithCerts === null || keysWithCerts.length === 0
              ? ''
              : keysWithCerts
                  .find((key: any) => key.id === Number(solrConfig.keyId))
                  .certs.filter((cert: any) => cert.id === Number(solrConfig.certId))
                  .map((cert: any) => `${cert.subjectDn} (${numberToHex(cert.serialNumber)})`)[0]
            : '',
        },
        {
          label: t('disabled'),
          value: solrConfig.disabled,
        },
      ]
    : []
}

function editSolrConfigDialog(t: TFunction, editDialogPropsSetter: any, setSolrConfig: React.Dispatch<any>, solrConfig: any, keysWithCerts: any[]) {
  const numberOfKeys = keysWithCerts.length
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('solrConfig.configureHeader'),
    labelGridSize: 3,
    onSubmit: SolrConfigService.updateSolrConfig,
    onSubmitExtraArgs: [t, setSolrConfig],
    data: (custom: any[]) => {
      return [
        {
          id: 'zookeeperHosts',
          value: solrConfig.zookeeperHosts?.join(', '),
          type: 'text',
          label: t('zookeeperHosts.label'),
          placeholder: t('zookeeperHosts.placeHolder'),
          autoComplete: 'zookeeperHosts',
        },
        {
          id: 'solrUrls',
          value: solrConfig.solrUrls?.join(', '),
          type: 'text',
          label: t('solrUrls.label'),
          placeholder: t('solrUrls.placeHolder'),
          autoComplete: 'solrUrls',
        },
        {
          id: 'username',
          value: solrConfig.username,
          type: 'text',
          label: t('username.text'),
          placeholder: t('username.text'),
          autoComplete: 'SolrUsername',
        },
        {
          id: 'password',
          value: solrConfig.password,
          type: 'password',
          label: t('password.text'),
          placeholder: t('password.text'),
          autoComplete: 'new-Password',
        },
        {
          groupLabel: t('tlsEnabled'),
          groupName: 'tlsEnabled',
          render: numberOfKeys > 0,
          type: 'switch',
          checkboxes: [
            {
              id: 'tlsEnabled',
              onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.toString(), custom[1], custom[2]]),
              value: custom[0] === 'true',
            },
          ],
        },
        {
          id: 'sslKey',
          value: solrConfig.sslKey,
          type: 'hidden',
          render: false,
        },
        {
          id: 'keyId',
          type: 'select',
          options: keysWithCerts.map((key) => ({ label: key.name, value: key.id })),
          value: custom[1],
          render: custom[0] === 'true',
          required: custom[0] === 'true',
          requiredMessage: t('invalidMessage', { label: 'Key' }),
          onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([custom[0], e.target.value, '']),
          label: t('keys.keyName'),
        },
        {
          id: 'certId',
          type: 'select',
          options:
            numberOfKeys > 0
              ? [
                  { label: 'Select a Certificate', value: '' },
                  ...keysWithCerts
                    ?.find((key) => key.id === Number(custom[1]))
                    ?.certs.map((cert: any) => ({ label: `${cert.subjectDn} (${numberToHex(cert.serialNumber)})`, value: cert.id })),
                ]
              : [],
          value: custom[2],
          render: custom[0] === 'true',
          required: custom[0] === 'true',
          onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([custom[0], custom[1], e.target.value]),
          label: t('certificate.text'),
          requiredMessage: t('invalidMessage', { label: 'Certificate' }),
        },
        {
          groupLabel: t('disabled'),
          groupName: 'disabled',
          type: 'switch',
          checkboxes: [
            {
              id: 'disabled',
              value: solrConfig.disabled,
            },
          ],
        },
      ]
    },
    initCustom: [solrConfig.tlsEnabled?.toString(), solrConfig.keyId || keysWithCerts[0]?.id, solrConfig.certId],
  }

  editDialogPropsSetter(editDialogProps)
}

function numberToHex(number: any) {
  return Number(number).toString(16).toUpperCase()
}

function deleteSolrConfigDialog(t: TFunction, confirmDialogPropsSetter: any, setSolrConfig: React.Dispatch<any>) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('solrConfig.deleteHeader'),
    body: t('solrConfig.deleteBody'),
    submitText: t('yes'),
    onSubmit: SolrConfigService.deleteSolrConfig,
    onSubmitExtraArgs: [t, setSolrConfig],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

async function testConnection(setSolrConnection: React.Dispatch<any>, setSolrCollections: React.Dispatch<any>) {
  await SolrConfigService.getCollections().then((solrCollections) => setSolrCollections(solrCollections))

  await SolrConfigService.getConnection().then((solrConnnection) => setSolrConnection(solrConnnection))
}

export const SolrConfigFlipCard = () => {
  const { t } = useTranslation()
  const [solrConfig, setSolrConfig] = useState({} as any)
  const [keysWithCerts, setKeysWithCerts] = useState([] as any[])
  const { userPermissions } = useContext(AppContext)
  const { editDialogPropsSetter, confirmDialogPropsSetter } = useContext(DialogContext)
  const viewPermission = Authorization.settings.solrConfig.view
  const editPermission = Authorization.settings.solrConfig.edit

  useEffect(() => {
    if (AuthService.hasPermission(userPermissions, viewPermission)) {
      SolrConfigService.getSolrConfig().then((solrConfig) => setSolrConfig(solrConfig))
    }
  }, [userPermissions, viewPermission])

  useEffect(() => {
    async function loadCerts() {
      await TlsService.getTlsKeysWithCerts(setKeysWithCerts)
      const config = await SolrConfigService.getSolrConfig()
      setSolrConfig(config)
    }

    if (AuthService.hasPermission(userPermissions, viewPermission)) {
      loadCerts()
    }
  }, [userPermissions, viewPermission])

  return (
    <FlipCard
      permissions={viewPermission}
      headerText={t('solrConfig.text')}
      headerLink='./setting/solr'
      flipFrontContent={
        <ButtonGroup vertical>
          <ProtectedButton
            permissions={editPermission}
            variant='link'
            className='m-1'
            onClick={() => editSolrConfigDialog(t, editDialogPropsSetter, setSolrConfig, solrConfig, keysWithCerts)}>
            {t('configure')}
          </ProtectedButton>
          <ProtectedButton
            permissions={editPermission}
            shouldRender={Object.keys(solrConfig).length > 0}
            variant='link'
            className='m-1'
            onClick={() => deleteSolrConfigDialog(t, confirmDialogPropsSetter, setSolrConfig)}>
            {t('solrConfig.deleteHeader')}
          </ProtectedButton>
        </ButtonGroup>
      }
    />
  )
}

export default SolrConfig
